import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { IAppliedFilters, ISummary } from 'features/Yucollect/utils/types';
import { IFailedPayload } from 'redux/types/BulkUploadAgent.types';

export interface AllocationListUniqueValue {
  id: string;
  name: string;
}

export interface FilterState {
  uniqueValues: string[];
  appliedFilters: IAppliedFilters;
  uniqueValuesLoader: boolean;
  error: IFailedPayload | null;
  summary: ISummary;
  selectedFilterByValues: Record<string, string[]>;
  searchTerm: string;
  allocationListUniqueValues: AllocationListUniqueValue[];
  idNameMap: Record<string, string>;
  currentColumnKey: string;
  columnIdNameMaps: Record<string, Record<string, string>>;
}

export const filterInitState: FilterState = {
  uniqueValues: [],
  appliedFilters: {},
  uniqueValuesLoader: false,
  error: null,
  summary: {
    sumWeightedAverage: '0',
    avgOutstanding: '0',
    sumOutstanding: '0',
    numberOfRecords: '0',
  },
  selectedFilterByValues: {},
  searchTerm: '',
  allocationListUniqueValues: [],
  idNameMap: {},
  currentColumnKey: '',
  columnIdNameMaps: {},
};

const FilterSlice = createSlice({
  name: 'filter',
  initialState: filterInitState,
  reducers: {
    setUniqueValues(state, action: PayloadAction<string[]>) {
      // Keep empty strings so they can be displayed as (Blanks)
      state.uniqueValues = action.payload;
      // Old code that filtered out empty strings:
      // state.uniqueValues = action.payload.filter((value) => value);
    },
    setIdNameMap(state, action: PayloadAction<Record<string, string>>) {
      state.idNameMap = action.payload;
      if (state.currentColumnKey && Object.keys(action.payload).length > 0) {
        state.columnIdNameMaps[state.currentColumnKey] = action.payload;
      }
    },
    setCurrentColumnKey(state, action: PayloadAction<string>) {
      state.currentColumnKey = action.payload;
    },
    resetUniqueValues: (state) => {
      state.uniqueValuesLoader = false;
      state.error = null;
      state.uniqueValues = [];
      state.idNameMap = {};
      state.currentColumnKey = '';
    },
    saveAppliedFilters: (state, action: PayloadAction<IAppliedFilters>) => {
      const newFilters = action.payload;

      state.appliedFilters = {
        ...state.appliedFilters,
        ...newFilters,
      };
    },
    resetAppliedFilters: (state) => {
      state.appliedFilters = {};
      state.selectedFilterByValues = {};
      state.searchTerm = '';
      state.columnIdNameMaps = {};
    },

    // TODO: Move this logic to respective files
    setFooterSummary: (state, action: PayloadAction<ISummary>) => {
      state.summary = action.payload;
    },
    setSelectedFilterByValues(
      state,
      action: PayloadAction<Record<string, string[]>>
    ) {
      state.selectedFilterByValues = action.payload;
    },
    setSearchTerm(state, action: PayloadAction<string>) {
      state.searchTerm = action.payload;
    },

    // TODO: Move this logic to respective files
    resetFooterSummary: (state) => {
      state.summary = {
        sumWeightedAverage: '0',
        avgOutstanding: '0',
        sumOutstanding: '0',
        numberOfRecords: '0',
      };
    },

    // Allocation List specific unique values with id and name
    setAllocationListUniqueValues(
      state,
      action: PayloadAction<AllocationListUniqueValue[]>
    ) {
      state.allocationListUniqueValues = action.payload;
    },
    resetAllocationListUniqueValues: (state) => {
      state.allocationListUniqueValues = [];
    },
  },
});

export const {
  setUniqueValues,
  setIdNameMap,
  setCurrentColumnKey,
  resetUniqueValues,
  saveAppliedFilters,
  resetAppliedFilters,
  setFooterSummary,
  setSelectedFilterByValues,
  setSearchTerm,
  resetFooterSummary,
  setAllocationListUniqueValues,
  resetAllocationListUniqueValues,
} = FilterSlice.actions;

export default FilterSlice.reducer;
